<script setup>
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import { useAnalysisStore } from '@/store/modules/analysis';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useAnalysisSajuContext, toAnalysisPerson } from '@/composables/saju-insight/useAnalysisSajuContext';
import { DEV_MODE } from '@/config/dev-mode';

import AlertComponent from '@/components/common/Alert.vue';
import HarmonyRelationSelector from './HarmonyRelationSelector.vue';
import HarmonyPersonSelector from './HarmonyPersonSelector.vue';
import HarmonyPersonListPopup from './HarmonyPersonListPopup.vue';

const { alertRef, showAlert, dialogTitle, dialogText, dialogNextUrl } = useDialogManager();

const props = defineProps({
  hasPermission: {
    type: Boolean,
    default: true,
  },
  mobile: {
    type: Boolean,
    default: false,
  },
  guestUserId: {
    type: [String, Number],
    default: null,
  },
});

const emit = defineEmits(['goPopAnalysis']);

const route = useRoute();
const context = useAnalysisSajuContext();
const subjectLocked = computed(() => !!context);
const dataStore = useAnalysisDataStore();
const analysisStore = useAnalysisStore();
const primarySajuStore = usePrimarySajuStore();
const { sajuData } = storeToRefs(dataStore);

/** STEP 1: 관계 유형 */
const selectedRelation = ref(null);

/** STEP 2: 대상자 목록 */
const persons = ref([null, null]);

/** STEP 3: 추가 궁합운 옵션 */
const selectedOptions = ref([]);

/** 저장목록 팝업 */
const showPersonListPopup = ref(false);
const addTargetIndex = ref(null);

/** '나' person의 guestUserId 결정 — 유효한 숫자 ID만 허용 */
const resolveMyGuestUserId = () => {
  const candidates = [
    props.guestUserId,
    route.params?.id,
    route.query?.id,
    primarySajuStore.mySajuId,
    primarySajuStore.primarySajuId,
  ];
  for (const v of candidates) {
    if (v === null || v === undefined || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

/** sajuData.inputdata에서 '나' person 객체 생성 */
const buildMyPerson = (inputdata, solarDate) => {
  if (!inputdata) return null;
  const guestUserId = resolveMyGuestUserId();
  if (!guestUserId) return null;
  const { name, gender, year } = inputdata;
  const age = year ? new Date().getFullYear() - Number(year) + 1 : null;
  return {
    guestUserName: name,
    name,
    gender: Number(gender) === 1 ? 'MEN' : 'WOMEN',
    solar: solarDate || '',
    age,
    guestUserId,
  };
};

/** primarySajuStore.mySaju에서 '나' person 객체 생성 (sajuData 없을 때 fallback) */
const buildMyPersonFromStore = () => {
  const mySaju = primarySajuStore.mySaju;
  if (!mySaju?.guestUserId) return null;
  const solarDate = mySaju.birthday || mySaju.solar || mySaju.birth || '';
  const yearStr = String(solarDate).slice(0, 4);
  const year = Number(yearStr);
  const age = Number.isFinite(year) && year > 0 ? new Date().getFullYear() - year + 1 : null;
  const genderRaw = mySaju.gender;
  const gender =
    genderRaw === 'MEN' || Number(genderRaw) === 1
      ? 'MEN'
      : genderRaw === 'WOMEN' || Number(genderRaw) === 0
        ? 'WOMEN'
        : 'MEN';
  return {
    guestUserName: mySaju.guestUserName || mySaju.name || '',
    name: mySaju.guestUserName || mySaju.name || '',
    gender,
    solar: solarDate,
    age,
    guestUserId: Number(mySaju.guestUserId),
  };
};

/** 현재 진입한 사주를 '나'로 기본 설정 */
const initDefaultPerson = () => {
  if (context) {
    persons.value[0] = toAnalysisPerson(context.saju.value);
    return;
  }
  const inputdata = sajuData.value?.inputdata;
  let myPerson = null;
  if (inputdata) {
    DEV_MODE && console.log('[궁합] inputdata:', JSON.parse(JSON.stringify(inputdata)));
    const solarDate = sajuData.value?.solar || '';
    myPerson = buildMyPerson(inputdata, solarDate);
  }
  if (!myPerson) {
    // sajuData가 없거나 guestUserId를 해석할 수 없으면 primarySajuStore.mySaju로 fallback
    myPerson = buildMyPersonFromStore();
  }
  if (myPerson) {
    persons.value[0] = myPerson;
  } else {
    DEV_MODE &&
      console.warn('[궁합] initDefaultPerson: myPerson 생성 실패', {
        hasInputdata: !!inputdata,
        propsId: props.guestUserId,
        routeParamsId: route.params?.id,
        routeQueryId: route.query?.id,
        mySajuId: primarySajuStore.mySajuId,
        primarySajuId: primarySajuStore.primarySajuId,
        mySaju: primarySajuStore.mySaju,
      });
  }
};

onMounted(async () => {
  if (analysisStore.consumeHarmonyResetSelection()) {
    // detail에서 복귀 시 관계/옵션/대상자는 초기화하되 '나'는 자동 채움
    persons.value = [null, null];
    selectedRelation.value = null;
    selectedOptions.value = [];
  }

  // '나' 슬롯 채우기 전에 mySajuId 보장 (퀵메뉴 등 query.id 없이 진입하는 경로 대응)
  if (!context && !primarySajuStore.mySajuId) {
    await primarySajuStore.fetchMySaju();
  }

  initDefaultPerson();
});

watch(
  () => sajuData.value?.inputdata,
  () => {
    if (!persons.value[0]) {
      initDefaultPerson();
    }
  },
);

/** primarySaju ID가 늦게 로드되는 경우 '나' 슬롯 재시도 */
watch(
  () => primarySajuStore.mySajuId,
  () => {
    if (!persons.value[0]) {
      initDefaultPerson();
    }
  },
);

/** 이미 선택된 사주 ID 목록 (중복 선택 방지) */
const excludeIds = computed(() => persons.value.filter(Boolean).map((p) => p.guestUserId));

/** 대상자 추가 팝업 열기 */
const openPersonList = (idx, source = '슬롯') => {
  if (context && idx === 0) return;
  if (!selectedRelation.value) {
    showAlert({ title: '관계를 먼저 선택해주세요.' });
    return;
  }
  addContentLogAPI({ contentType: 'ap_harmony_btn_add_person_opt3', option3: source });
  addTargetIndex.value = idx;
  showPersonListPopup.value = true;
};

/** 대상자 선택 완료 */
const onSelectPerson = (person) => {
  const idx = addTargetIndex.value;
  if (context && idx === 0) return;
  if (idx !== null && idx < persons.value.length) {
    persons.value[idx] = { ...person };
  } else {
    persons.value.push({ ...person });
  }
};

/** 대상자 제거 — index 0('나')은 null로만 비우고, 나머지는 splice */
const onRemovePerson = (idx) => {
  if (context && idx === 0) return;
  if (idx === 0) {
    persons.value[0] = null;
    return;
  }
  if (persons.value.length <= 2) {
    persons.value[idx] = null;
  } else {
    persons.value.splice(idx, 1);
  }
};

const MAX_PERSONS = 4;
const canAddMore = computed(() => persons.value.length < MAX_PERSONS);

/** 비어있는 첫 번째 슬롯 인덱스 (없으면 null) */
const firstEmptyIndex = computed(() => {
  const idx = persons.value.findIndex((p) => !p);
  return idx === -1 ? null : idx;
});

/** 궁합 시작 가능 여부 — 모든 슬롯이 채워져야 시작 가능 */
const canStart = computed(() => {
  return selectedRelation.value && persons.value.length >= 2 && persons.value.every(Boolean);
});

/** 관계 유형 id → API category 매핑 */
const CATEGORY_MAP = {
  lover: 'LOVER',
  friend: 'FRIEND',
  business: 'BUSINESS',
  family: 'FAMILY',
};
/** 관계 id → 한글 라벨 (로그 option3용) */
const HARMONY_RELATION_LABEL = { lover: '연인', friend: '친구', business: '비즈니스', family: '가족' };

/** 선택이 변경될 때마다 store에 결제 데이터 자동 동기화 (PC: CoverPanel 버튼 대응) */
const syncPaymentData = () => {
  if (context && !context.isActive()) return;
  const validPersons = persons.value.filter(Boolean);
  if (context && Number(validPersons[0]?.guestUserId) !== Number(context.saju.value?.guestUserId)) return;
  if (!selectedRelation.value || validPersons.length < 2) {
    analysisStore.setHarmonyPaymentData(null);
    return;
  }

  const memberGuestUserIds = validPersons.map((p) => Number(p.guestUserId));
  if (memberGuestUserIds.some((id) => !Number.isFinite(id) || id <= 0)) {
    analysisStore.setHarmonyPaymentData(null);
    DEV_MODE && console.warn('[궁합] 유효하지 않은 guestUserId 감지, 결제 데이터 동기화 중단', memberGuestUserIds);
    return;
  }
  const category = CATEGORY_MAP[selectedRelation.value] || 'LOVER';

  analysisStore.setHarmonyPaymentData({
    category,
    memberGuestUserIds,
    persons: validPersons,
  });

  DEV_MODE && console.log('[궁합] 결제 데이터 동기화', { category, memberGuestUserIds });
};

watch([selectedRelation, persons], syncPaymentData, { deep: true });

/** 관계유형·대상자 선택 상태를 store에 독립 동기화 (검증 메시지 분기용) */
watch(
  [selectedRelation, persons],
  () => {
    const category = selectedRelation.value ? CATEGORY_MAP[selectedRelation.value] || null : null;
    const personCount = persons.value.filter(Boolean).length;
    analysisStore.setHarmonySelectionState(category, personCount);
  },
  { deep: true, immediate: true },
);

/** 궁합 분석 시작 (모바일: 부모에서 호출) */
const startHarmony = () => {
  // 모바일 커버 시작 무료 조회 로그(카테고리별). 카테고리 선택 시 발화(PC=CoverPanelHarmony 와 동일 기준).
  if (props.mobile && selectedRelation.value) {
    addContentLogAPI({
      contentType: 'ap_harmony_btn_cover_start',
      option3: HARMONY_RELATION_LABEL[selectedRelation.value] || selectedRelation.value,
    });
  }
  if (!canStart.value) return;

  addContentLogAPI({
    contentType: 'ap_harmony_btn_start',
  });

  syncPaymentData();

  // detail 페이지로 이동
  analysisStore.clickMainMenu('/nangman-harmony');
};

defineExpose({ canStart, startHarmony });
</script>

<template>
  <div class="si-overview__toc harmony-service-select" :class="{ mobile }">
    <h2 class="harmony-service-select__title">궁합 서비스 선택</h2>

    <div class="harmony-service-select__content">
      <HarmonyRelationSelector v-model="selectedRelation" />

      <HarmonyPersonSelector :persons="persons" :lock-subject="subjectLocked" @add="openPersonList" @remove="onRemovePerson" />

      <!-- <HarmonyOptionSelector v-model="selectedOptions" /> -->
    </div>

    <div class="harmony-service-select__footer">
      <button
        v-if="canAddMore"
        class="harmony-service-select__add-btn"
        @click="openPersonList(firstEmptyIndex ?? persons.length, '버튼')"
      >
        + 대상자 추가하기
      </button>
    </div>

    <HarmonyPersonListPopup
      :isVisible="showPersonListPopup"
      :excludeIds="excludeIds"
      @close="showPersonListPopup = false"
      @select="onSelectPerson"
    />

    <Teleport to="body">
      <AlertComponent
        ref="alertRef"
        :title="dialogTitle"
        :text="dialogText"
        :next-url="dialogNextUrl"
      />
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
/* 서비스 선택 영역 */
.harmony-service-select {
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
}

.harmony-service-select__title {
  width: 100%;
  max-width: 500px;
  text-align: center;
  color: var(--Gray-800, #333);
  font-size: 20px;
  font-weight: 700;
  line-height: 24px;
}

.harmony-service-select__content {
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 32px;

  > * {
    width: 100%;
  }
}

.harmony-service-select__divider {
  height: 1px;
  background: var(--Gray-200, #f5f5f5);
  margin: 24px 0;
}

.harmony-service-select__footer {
  margin-top: 28px;
  width: 100%;
  max-width: 500px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  position: fixed;
  bottom: 0;
  padding: 16px 16px;
  margin-top: auto;
  background: transparent;
  pointer-events: none;
}

.harmony-service-select__add-btn {
  display: flex;
  margin: 0 auto;
  padding: 6px 15px;
  justify-content: center;
  align-items: center;
  border-radius: 100px;
  border: none;
  background: var(--Orange-400, #ff6b6b);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  line-height: 18px;
  cursor: pointer;
  transition: background 0.15s ease;
  pointer-events: auto;

  &:hover {
    background: #e85d5d;
  }
}

/* mobile */
.harmony-service-select.mobile {
  padding: 0px 0 60px;
  height: 100%;
  overflow: hidden;

  .harmony-service-select__title {
    display: none;
  }

  .harmony-service-select__content {
    padding: 0 16px 140px;
    flex: 1;
    overflow-x: hidden;
    overflow-y: auto;
  }

  .harmony-service-select__footer {
    position: fixed;
    bottom: calc(52px + var(--safe-area-bottom, 0px));
    left: 0;
    right: 0;
    margin: 0 auto;
    padding: 16px 16px;
    background: transparent;
    z-index: 40;
    pointer-events: none;
  }
}
</style>
